if (!customElements.get('product-inventory')) {
  // Decorative clock (see assets/dispatch-countdown.css). The sweeping hand is a
  // pure CSS animation for visual life — it is NOT tied to the real countdown value.
  // Truck icon for the NZ delivery line (see renderShippingMessage) — same outline truck as the
  // Overkill product page's delivery block.
  const TRUCK_SVG = '<svg class="ship-line__icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M13.75 6.25H17.0766C17.2011 6.24994 17.3228 6.28709 17.4261 6.35669C17.5294 6.42629 17.6095 6.52517 17.6562 6.64063L18.75 9.375"/><path d="M1.25 11.25H13.75"/><path d="M14.375 16.875C15.4105 16.875 16.25 16.0355 16.25 15C16.25 13.9645 15.4105 13.125 14.375 13.125C13.3395 13.125 12.5 13.9645 12.5 15C12.5 16.0355 13.3395 16.875 14.375 16.875Z"/><path d="M5.625 16.875C6.66053 16.875 7.5 16.0355 7.5 15C7.5 13.9645 6.66053 13.125 5.625 13.125C4.58947 13.125 3.75 13.9645 3.75 15C3.75 16.0355 4.58947 16.875 5.625 16.875Z"/><path d="M12.5 15H7.5"/><path d="M13.75 9.375H18.75V14.375C18.75 14.5408 18.6842 14.6997 18.5669 14.8169C18.4497 14.9342 18.2908 15 18.125 15H16.25"/><path d="M3.75 15H1.875C1.70924 15 1.55027 14.9342 1.43306 14.8169C1.31585 14.6997 1.25 14.5408 1.25 14.375V5.625C1.25 5.45924 1.31585 5.30027 1.43306 5.18306C1.55027 5.06585 1.70924 5 1.875 5H13.75V13.232"/></svg>';
  const DISPATCH_CLOCK_SVG = '<svg class="dispatch-clock" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="8.5" stroke="currentColor" stroke-width="2"/><line class="dispatch-clock__hand" x1="12" y1="12" x2="12" y2="6.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

  class ProductInventory extends HTMLElement {
    constructor() {
      super();
      window.initLazyScript(this, this.initLazySection.bind(this));
    }

    initLazySection() {
      // Elements
      this.inventoryNotice = this.querySelector('.js-inventory-notice');
      this.shippingMessage = this.querySelector('.js-shipping-message');
      this.customUrgencyLine = this.querySelector('.js-custom-urgency-line');

      // Data
      this.variantInventory = this.getVariantInventory();
      this.freeShipThreshold = parseInt(this.dataset.freeShipThreshold || '9900', 10);
      this.hideFreeShip = (this.dataset.hideFreeShip ?? '').toString().trim().toLowerCase() === 'true';
      this.currentPriceCents = parseInt(this.dataset.initialPrice || '0', 10);

      // Bind to variant change. Standard product template has a <variant-picker>
      // element; the bespoke hero templates don't — they dispatch on:variant:change
      // on document instead, so fall back to a document-level listener there.
      const variantPicker = this.closest('.product-info')?.querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      } else {
        document.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }

      // Initial render
      this.updateInventory(
        parseInt(this.dataset.inventoryQuantity || '0', 10),
        this.dataset.variantAvailable === 'true',
        this.dataset.inventoryPolicy,
        this.currentPriceCents,
        this.dataset.inventoryManagement
      );
    }

    getVariantInventory() {
      // Standard template scopes the blob inside .product-info; the bespoke hero
      // templates have no such wrapper, so fall back to a document-level lookup.
      const dataEl = this.closest('.product-info')?.querySelector('.js-inventory-data')
        || document.querySelector('.js-inventory-data');
      if (!dataEl) return [];
      try { return JSON.parse(dataEl.textContent); } catch (e) { return []; }
    }

    handleVariantChange(evt) {
      if (evt.detail.variant) {
        const v = evt.detail.variant;
        const inventory = this.variantInventory.find((variant) => variant.id === v.id);
        if (inventory) {
          this.currentPriceCents = typeof v.price === 'number' ? v.price : this.currentPriceCents;
          this.updateInventory(inventory.inventory_quantity, inventory.available, inventory.inventory_policy, this.currentPriceCents, inventory.inventory_management);
        }
      } else {
        this.updateInventory(0, false);
        this.hidden = true;
      }
    }

    updateInventory(count, available, inventoryPolicy, priceCents = this.currentPriceCents, inventoryManagement = this.dataset.inventoryManagement) {
      // Never trust a raw count — Shopify can report negatives for oversold /
      // continue-selling variants, and a negative would flow into "Only -3 left".
      count = Math.max(0, parseInt(count, 10) || 0);

      // Scarcity ("Order fast! Only N left") only applies to variants Shopify
      // actively tracks AND denies overselling on. Continue-selling or untracked
      // variants are effectively unlimited — never show scarcity for them.
      const veryLow = parseInt(this.dataset.thresholdVeryLow, 10) || 0;
      const mgmtKnown = inventoryManagement !== undefined && inventoryManagement !== null && inventoryManagement !== '';
      const scarcityEligible = mgmtKnown
        ? (inventoryManagement === 'shopify' && inventoryPolicy === 'deny')
        : (inventoryPolicy !== 'continue');
      const showScarcity = scarcityEligible && count > 0 && count <= veryLow;

      let inventoryLevel;
      if (count <= 0) {
        inventoryLevel = (inventoryPolicy === 'continue') ? 'backordered' : (available ? 'in_stock' : 'none');
      } else if (count <= parseInt(this.dataset.thresholdVeryLow, 10)) {
        inventoryLevel = 'very_low';
      } else if (count <= parseInt(this.dataset.thresholdLow, 10)) {
        inventoryLevel = 'low';
      } else {
        inventoryLevel = 'normal';
      }

      const onPdp = !!this.closest('.product-info');
      if (onPdp && inventoryLevel === 'backordered') {
        this._shippingActive = false;
        this.hidden = true;
        this.shippingMessage?.classList.remove('show');
        this.classList.remove('ship-block-mode');
        return;
      }
      // The overnight line (merged or blue block) never shows on a pre-order/backordered variant.
      this._overnightOk = inventoryLevel !== 'backordered';

      // 1. COMBINED SHIPPING & URGENCY (The Dynamic Text)
      if (available === false || !this.shippingMessage) {
        this._shippingActive = false;
        this.shippingMessage?.classList.remove('show');
        this.classList.remove('ship-block-mode');
      } else {
        this._shippingActive = true;
        // Scarcity highlight — only for tracked, deny-overselling variants at/below
        // the very-low threshold (see showScarcity above). Cached so the per-minute
        // dispatch-countdown tick can rebuild the line without a full recompute.
        this._scarcityText = showScarcity ? `<strong>Only ${count} left!</strong> ` : '';
        this.renderShippingMessage();
        // The dispatch line is a live NZ-time countdown; refresh it each minute.
        // DispatchCountdown owns the shared ticker; bind once.
        if (!this._dispatchTickBound && window.DispatchCountdown) {
          this._dispatchTickBound = true;
          window.DispatchCountdown.onTick(this.renderShippingMessage.bind(this));
        }
      }

      // 2. PILL LOGIC
      if (this.dataset.showNotice === 'always' || (this.dataset.showNotice === 'low' && inventoryLevel.includes('low')) || inventoryLevel === 'backordered') {
        this.hidden = false;
        if (this.inventoryNotice) {
          this.inventoryNotice.innerText = (inventoryLevel === 'backordered')
          ? (this.dataset.textNoStockBackordered || 'Pre-order').replace(/<[^>]*>/g, '').trim()
          : (available ? 'In stock' : 'Out of stock');
        }
      } else {
        this.hidden = true;
      }

      // 3. TARGET THE BUY BUTTON (From your HTML snippet)
      const buyBtn = this.closest('.product-info')?.querySelector('button[name="add"]');
      if (buyBtn) {
        if (showScarcity) {
          buyBtn.style.setProperty('background-color', '#d0021b', 'important'); // Urgency Red
          buyBtn.style.setProperty('border-color', '#d0021b', 'important');
        } else {
          buyBtn.style.backgroundColor = '';
          buyBtn.style.borderColor = '';
        }
      }

      // Remove the old extra line
      if (this.customUrgencyLine) this.customUrgencyLine.style.display = 'none';
    }

    // AU headline dash (2026-10-14): the " -" between the two headline halves shows only while they sit on
    // one line. Once the second half wraps (phones), the dash is hidden with visibility (not display) so the
    // measured widths stay put and the line can't flip back to one line. Re-run on resize and once web
    // fonts have loaded; the per-minute re-render calls it again anyway.
    syncHeadDash() {
      const hd = this.shippingMessage && this.shippingMessage.querySelector('.ship-line__head');
      if (!hd) return;
      const segs = hd.querySelectorAll('.ship-line__seg');
      if (!hd.querySelector('.ship-line__dash') || segs.length < 2) return;
      hd.classList.toggle('ship-line__head--wrapped', segs[1].offsetTop > segs[0].offsetTop);
    }

    // Builds "<scarcity> - <dispatch line>" from the cached scarcity text plus the
    // live NZ-time dispatch state. Called on variant change and on each minute tick.
    renderShippingMessage() {
      if (!this.shippingMessage || !this._shippingActive) return;
      const scarcityText = this._scarcityText || '';
      let shippingInfo;
      // Ship-from label, market-aware (window.SHIP_WAREHOUSE_LABEL, set in theme.liquid):
      // "our Northland warehouse" normally, "our warehouse" in the AU market. Falls back to the
      // NZ wording if the global isn't set.
      const warehouseLabel = window.SHIP_WAREHOUSE_LABEL || 'our Northland warehouse';
      // NZ delivery line (TEST 2026-10-10): in the NZ market, for non-oversize, in-stock items,
      // the ships line becomes a two-line delivery line with a truck icon — "Overnight NZ-wide
      // delivery" over the existing ships-day line. AU, oversize (window.SHIP_OVERSIZE, set in
      // theme.liquid: rods / combos / Oversize-tagged) and pre-order keep the plain line.
      const shipMarket = window.SHIP_MARKET || 'NZ';
      const lineOk = !window.SHIP_OVERSIZE && this._overnightOk !== false;
      const nzBlock = shipMarket !== 'AU' && lineOk;
      // AU (TEST 2026-10-10): same delivery line, headline "Tracked delivery in X–Y business days" where X–Y is the
      // east-coast band from the AU market metafield (window.SHIP_AU_BAND, set in theme.liquid), plus a small
      // "ETA by state" link to the AU FAQ. No band available = plain line.
      const auBlock = shipMarket === 'AU' && lineOk && !!window.SHIP_AU_BAND;
      // NZ oversize (2026-10-10): rods, other bulky items and tagged freight items get the same block
      // with a measured headline (window.SHIP_OVERSIZE_KIND from theme.liquid: 'rod' | 'bulky' | 'freight').
      // Same show rules as the overnight line.
      const kind = window.SHIP_OVERSIZE_KIND || '';
      const nzBulky = shipMarket !== 'AU' && !!window.SHIP_OVERSIZE && this._overnightOk !== false && (kind === 'rod' || kind === 'bulky' || kind === 'freight');
      const bang = (nzBlock || auBlock || nzBulky) ? '' : '!';
      const dc = window.DispatchCountdown;
      // getTomorrow (2026-10-11): true only in the same-day-dispatch state (NZ weekday before the cutoff,
      // not a holiday) when tomorrow is also a dispatch day, i.e. Mon-Thu and not the day before a public
      // holiday. Drives the "Get it tomorrow, NZ-wide" headline on the plain NZ overnight tier only.
      let getTomorrow = false;
      if (dc) {
        const st = dc.state();
        getTomorrow = !!(st.sameDay && st.tomorrowDispatch);
        // Friday / weekend / holiday sub-line (2026-10-16), NZ overnight tier only. The plain copy below
        // ("…and it ships today" / "Ships tomorrow from…") implies next-day delivery, so it only shows when
        // today is a working day AND the courier delivers the calendar day after dispatch. Otherwise
        // (Fri, Thu after the cutoff, Sat, Sun, public holidays and their eves) the sub-line says when
        // the parcel actually lands:
        //   Fri before cutoff:            "Order in the next 2 hr 5 min - ships today, delivered Monday"
        //   Thu after cutoff:             "Order now - ships Friday, delivered Monday"
        //   Fri after cutoff / Sat / Sun: "Order now - ships Monday, delivered Tuesday"
        // Headline stays "Overnight NZ-wide delivery". Mon-Wed, and Thu before the cutoff, are untouched.
        // Rods (headline "Overnight delivery (South Island 2 days)") get the same rule with both legs named:
        //   "… ships today, delivered Monday (South Island Tuesday)". Bulky (1-2 day) and freight don't
        // claim overnight, so they keep the plain copy.
        const rodTier = nzBulky && kind === 'rod';
        const plainOk = st.deliveryOvernight && (st.sameDay || (st.todayDispatch && st.dayLabel === 'tomorrow'));
        const gapDelivery = (nzBlock || rodTier) && st.reliable && st.deliveryLabel !== 'soon' && !plainOk;
        const southIsland = (rodTier && st.deliveryLabel2 !== 'soon') ? ` <span class="ship-line__keep">(South Island ${st.deliveryLabel2})</span>` : '';
        if (st.sameDay && gapDelivery) {
          const badgeClass = st.urgent ? 'dispatch-badge dispatch-badge--urgent' : 'dispatch-badge';
          shippingInfo = `<strong>Order</strong> in the next ${DISPATCH_CLOCK_SVG}<span class="${badgeClass}">${dc.format(st.secsToCutoff)}</span> - <strong>ships today</strong>, <span class="ship-line__keep">delivered ${st.deliveryLabel}</span>${southIsland}`;
        } else if (gapDelivery) {
          shippingInfo = `<strong>Order now</strong> - ships <strong>${st.dispatchName}</strong>, <span class="ship-line__keep">delivered ${st.deliveryLabel}</span>${southIsland}`;
        } else if (st.sameDay) {
          const badgeClass = st.urgent ? 'dispatch-badge dispatch-badge--urgent' : 'dispatch-badge';
          shippingInfo = `<strong>Order</strong> in the next ${DISPATCH_CLOCK_SVG}<span class="${badgeClass}">${dc.format(st.secsToCutoff)}</span> and it <strong>ships today</strong>`;
        } else {
          shippingInfo = `Ships <strong>${st.dayLabel}</strong> from ${warehouseLabel}${bang}`;
        }
      } else {
        // Helper not loaded yet — safe, generic fallback (no same-day overpromise).
        shippingInfo = `Ships <strong>tomorrow</strong> from ${warehouseLabel}${bang}`;
      }
      if (nzBlock || auBlock || nzBulky) {
        // Overkill-style delivery line (2026-10-10 v5): no box — truck icon, bold caps headline in the
        // IN STOCK green, ships line (incl. "Only N left!") beneath in black. Sits on its own row under
        // the pills (see dispatch-countdown.css). NZ: "Overnight NZ-wide delivery". AU: see the auBlock
        // branch below (dispatch promise headline + transit band sub-line + "ETA by state" link).
        // The band phrase is kept together so a phone-width wrap reads "TRACKED DELIVERY IN / 3–6 BUSINESS DAYS".
        let head = 'Overnight NZ-wide delivery';
        let headClass = 'ship-line__head';
        // NZ overnight tier only (2026-10-11): "Get it tomorrow, NZ-wide" while an order placed now ships
        // today and tomorrow is a working day. Rod / bulky / freight / AU headlines are untouched below.
        let text = `${scarcityText}${shippingInfo}`;
        if (nzBlock && getTomorrow) head = 'Get it tomorrow, NZ-wide';
        if (auBlock) {
          // AU (2026-10-14): the headline carries the dispatch promise, the sub-line the transit band.
          // Before the NZ cutoff: "SHIPS TODAY - ORDER WITHIN <countdown>" over "Tracked to your door in
          // 3–6 business days - ETA by state". After it: "SHIPS <TOMORROW | MONDAY | Mon 5 Jan> - TRACKED TO
          // YOUR DOOR" over "Arrives 3–6 business days after dispatch - ETA by state". The two headline halves
          // are non-breaking segments so a phone-width wrap falls after the dash. Scarcity ("Only N left!")
          // keeps its place at the start of the sub-line. The band comes from window.SHIP_AU_BAND as before.
          const auSt = dc ? dc.state() : null;
          // Plain hyphen in the range ("3-6"); the band snippet emits an en dash.
          const auBand = String(window.SHIP_AU_BAND).replace(/[\u2013\u2014]/g, '-');
          const band = `<span class="ship-line__keep">${auBand} business days</span>`;
          // The " -" between the halves lives in its own span: shown on one line, hidden once the second
          // half wraps (see syncHeadDash below), so phones read "SHIPS TODAY" over "ORDER WITHIN 10 MIN".
          const dash = '<span class="ship-line__dash"> -</span>';
          if (auSt && auSt.sameDay) {
            const auBadge = auSt.urgent ? 'dispatch-badge dispatch-badge--urgent' : 'dispatch-badge';
            head = `<span class="ship-line__seg">Ships today${dash}</span> <span class="ship-line__seg">Order within ${DISPATCH_CLOCK_SVG}<span class="${auBadge}">${dc.format(auSt.secsToCutoff)}</span></span>`;
            text = `${scarcityText}Tracked to your door in ${band}`;
          } else {
            const auDay = auSt ? auSt.dayLabel : 'tomorrow';
            head = `<span class="ship-line__seg">Ships ${auDay}${dash}</span> <span class="ship-line__seg">Tracked to your door</span>`;
            text = `${scarcityText}Arrives ${band} after dispatch`;
          }
        }
        else if (nzBulky && kind === 'rod') head = '<span class="ship-line__seg">Overnight delivery</span> <span class="ship-line__seg">(South Island 2 days)</span>';
        else if (nzBulky && kind === 'freight') head = '<span class="ship-line__seg">Freight delivery</span> <span class="ship-line__seg">in 3-7 business days</span>';
        else if (nzBulky) {
          // Two non-breaking segments: one line where it fits; on phones the break falls before the
          // bracket so "(SOUTH ISLAND 2-4)" sits on its own line (see dispatch-countdown.css).
          head = '<span class="ship-line__seg">1-2 day delivery</span> <span class="ship-line__seg">(South Island 2-4)</span>';
        }
        const link = auBlock ? ` <span class="ship-line__keep">- <a class="ship-line__link" href="${window.SHIP_AU_FAQ_URL || '/pages/help-centre-faqs'}">ETA by state</a></span>` : '';
        this.shippingMessage.innerHTML = `<span class="ship-line">${TRUCK_SVG}<span class="ship-line__copy"><strong class="${headClass}">${head}</strong><span class="ship-line__text">${text}${link}</span></span></span>`;
        if (auBlock) {
          this.syncHeadDash();
          if (!this._dashWatchBound) {
            this._dashWatchBound = true;
            window.addEventListener('resize', () => this.syncHeadDash());
            if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.syncHeadDash());
          }
        }
      } else {
        this.shippingMessage.innerHTML = ` - ${scarcityText}${shippingInfo}`;
      }
      this.classList.toggle('ship-block-mode', nzBlock || auBlock || nzBulky);
      this.shippingMessage.classList.add('show');
    }
  }
  customElements.define('product-inventory', ProductInventory);
}